import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, AlertCircle, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import { useCart } from "../lib/cart";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ state: "checking", data: null });
  const { clear } = useCart();
  const polled = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus({ state: "error", data: null }); return; }
    let alive = true;
    const tick = async () => {
      if (!alive) return;
      try {
        const r = await api.get(`/checkout/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setStatus({ state: "paid", data: r.data });
          clear();
          return;
        }
        if (r.data.status === "expired") { setStatus({ state: "expired", data: r.data }); return; }
        polled.current += 1;
        if (polled.current >= 8) { setStatus({ state: "pending", data: r.data }); return; }
        setTimeout(tick, 2000);
      } catch (e) {
        setStatus({ state: "error", data: null });
      }
    };
    tick();
    return () => { alive = false; };
  }, [sessionId]); // eslint-disable-line

  return (
    <section className="min-h-screen pt-40 pb-24 px-6 flex items-center justify-center" data-testid="checkout-success-page">
      <div className="max-w-xl text-center">
        {status.state === "paid" && (
          <>
            <CheckCircle2 size={48} className="text-[var(--twr-bronze)] mx-auto" strokeWidth={1.2} />
            <p className="eyebrow mt-6">Payment Received</p>
            <h1 className="font-serif text-4xl sm:text-5xl mt-4 leading-tight">Thank you.</h1>
            <p className="text-[var(--twr-warm-white)]/70 mt-6 leading-relaxed">
              Your order has been received. A confirmation will arrive in your inbox shortly.
            </p>
            <p className="text-[var(--twr-warm-white)]/55 text-sm mt-3">
              Total · <span className="text-[var(--twr-bronze)] font-serif">${((status.data?.amount_total || 0) / 100).toFixed(2)}</span>
            </p>
            <div className="flex gap-3 justify-center mt-10">
              <Link to="/shop" className="btn-ghost">Continue Shopping</Link>
              <Link to="/" className="btn-bronze">Return Home <ChevronRight size={14} /></Link>
            </div>
          </>
        )}
        {status.state === "checking" && (
          <>
            <div className="monogram mx-auto" style={{ width: 64, height: 64, fontSize: 16 }}>TWR</div>
            <p className="eyebrow mt-6">Verifying</p>
            <h1 className="font-serif text-3xl mt-4">Confirming your payment…</h1>
            <p className="text-[var(--twr-warm-white)]/60 mt-4">Just a moment.</p>
          </>
        )}
        {status.state === "pending" && (
          <>
            <p className="eyebrow">Processing</p>
            <h1 className="font-serif text-3xl mt-4">Your payment is still processing.</h1>
            <p className="text-[var(--twr-warm-white)]/60 mt-4">You'll receive a confirmation email when it completes.</p>
          </>
        )}
        {(status.state === "expired" || status.state === "error") && (
          <>
            <AlertCircle size={42} className="text-[var(--twr-bronze)] mx-auto" />
            <p className="eyebrow mt-6">Hmm</p>
            <h1 className="font-serif text-3xl mt-4">We couldn't confirm that session.</h1>
            <Link to="/cart" className="btn-bronze mt-8 inline-flex">Back to Cart <ChevronRight size={14} /></Link>
          </>
        )}
      </div>
    </section>
  );
}
